import React from 'react'
import { motion } from 'framer-motion'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { ImageWithFallback } from '../../utils/ImageWithFallback'
import {
  CheckCircle,
  Star,
  ArrowRight,
  Play,
  Leaf,
  Users,
  Award,
  Calendar,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import slider1 from '../../assets/sliders/slide.png'

export function HeroComponent() {
  const navigate = useNavigate()
  const stats = [
    {
      number: '500+',
      label: 'Happy Clients',
      icon: <Users className="w-5 h-5" />,
    },
    {
      number: '15+',
      label: 'Years Experience',
      icon: <Award className="w-5 h-5" />,
    },
    {
      number: '1000+',
      label: 'Projects Completed',
      icon: <Leaf className="w-5 h-5" />,
    },
    {
      number: '100%',
      label: 'Satisfaction Rate',
      icon: <Star className="w-5 h-5" />,
    },
  ]

  const features = [
    'Licensed & Insured Professionals',
    'Free Consultation & Estimates',
    '100% Satisfaction Guarantee',
    'Eco-Friendly Practices',
  ]

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <ImageWithFallback
          src={slider1}
          alt="Beautiful landscaped garden"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <motion.div
        className="absolute top-20 right-20 w-32 h-32 bg-green-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        className="absolute bottom-32 left-20 w-24 h-24 bg-green-400/20 rounded-full blur-2xl"
        animate={{
          x: [0, -20, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Badge className="mb-6 bg-green-600/90 text-white border-green-500">
                <Leaf className="w-4 h-4 mr-2" />
                #1 Landscaping Service in the Area
              </Badge>
            </motion.div>

            <motion.h1
              className="text-5xl lg:text-7xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Transform Your
              <span className="text-green-400 block">Outdoor Space</span>
            </motion.h1>

            <motion.p
              className="text-xl text-gray-200 mb-8 leading-relaxed max-w-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Professional landscaping services that bring your vision to life.
              From design to maintenance, we create stunning outdoor
              environments.
            </motion.p>

            {/* Features List */}
            <motion.div
              className="mb-8 space-y-3"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-center"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  <span className="text-gray-200">{feature}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg group"
                onClick={() => {
                  navigate('/contact')
                }}
              >
                Get Free Estimate
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg group"
                onClick={() => {
                  navigate('/services')
                }}
              >
                <Play className="mr-2 w-5 h-5" />
                View Our Work
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              className="grid grid-cols-2 lg:grid-cols-4 gap-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="flex items-center justify-center mb-2 text-green-400">
                    {stat.icon}
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-300">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Hero Image/Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Quick Quote Calculator
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-200 mb-2">
                      Property Size
                    </label>
                    <select className="w-full px-4 py-3 rounded-lg bg-white/90 text-gray-800 border-0 focus:ring-2 focus:ring-green-500 ">
                      <option>Small (under 0.25 acres)</option>
                      <option>Medium (0.25 - 0.5 acres)</option>
                      <option>Large (0.5 - 1 acre)</option>
                      <option>Extra Large (1+ acres)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-200 mb-2">
                      Service Type
                    </label>
                    <select className="w-full px-4 py-3 rounded-lg bg-white/90 text-gray-800 border-0 focus:ring-2 focus:ring-green-500">
                      <option>Lawn Maintenance</option>
                      <option>Garden Design</option>
                      <option>Landscape Installation</option>
                      <option>Tree Services</option>
                      <option>Full Property Makeover</option>
                    </select>
                  </div>

                  <Link to={'/contact'}>
                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-3">
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Consultation
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="text-center pt-6 border-t border-white/20">
                <p className="text-green-300 font-medium">
                  Free consultation & estimate
                </p>
                <p className="text-sm text-gray-300">
                  No obligation • 24-48 hour response
                </p>
              </div>
            </div>

            {/* Floating testimonial */}
            <motion.div
              className="absolute -bottom-25 left-0 md:-left-6 bg-white rounded-xl p-4 shadow-xl max-w-xs"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.5 }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="flex items-center mb-2">
                <div className="flex text-yellow-400 mr-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span className="text-sm font-medium text-gray-800">5.0</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                "Incredible transformation! Our yard went from ordinary to
                extraordinary."
              </p>
              <p className="text-xs text-gray-500">- Sarah M., Happy Client</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-24 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2"></div>
        </div>
      </motion.div>
    </section>
  )
}
